import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, Building2, Sparkles, Zap, Users, LineChart, Compass, Puzzle } from "lucide-react";
import { usePageSEO } from "@/lib/seo";

const propositie = [
  { icon: Zap, label: "Snelheid & wendbaarheid", body: "Wij starten binnen weken een pilot. Kort de lijn, snel bijsturen." },
  { icon: Users, label: "Kennis + ervaring", body: "Professionals en ervaringsdeskundigen samen in één team." },
  { icon: LineChart, label: "Zorgvuldige evaluatie", body: "We meten wat relevant is voor u en de deelnemers — geen KPI-theater." },
  { icon: Compass, label: "Mensgericht", body: "Onze aanpak zet de mens boven het protocol." },
  { icon: Puzzle, label: "Aanvullend, niet dubbel", body: "We sluiten aan op bestaande netwerken en zorgstructuren." },
];

const mogelijkheden = [
  {
    icon: HeartHandshake,
    label: "Doorverwijzen",
    body: "Heeft u iemand in beeld die praktische ondersteuning nodig heeft? Wij nemen laagdrempelig contact op met de betrokkene.",
    cta: "Iemand doorverwijzen",
    to: "/contact",
  },
  {
    icon: Building2,
    label: "Projectpartnerschap",
    body: "Samen een maatschappelijk project ontwikkelen of uitvoeren — van pilot tot opschaling, binnen één van onze programma's.",
    cta: "Bespreek een pilot",
    to: "/contact",
  },
  {
    icon: Sparkles,
    label: "Financiering & impact",
    body: "Als fonds, bedrijf of vermogende partner bijdragen aan concrete maatschappelijke impact op preventie, herstel of participatie.",
    cta: "Steun een programma",
    to: "/steun",
  },
];

export default function VoorGemeentenPage() {
  usePageSEO({
    title: "Voor gemeenten & partners | Stichting FIN",
    description:
      "Stichting FIN werkt samen met gemeenten, welzijnsorganisaties, woningcorporaties, zorgorganisaties, fondsen en bedrijven aan wendbare projecten op het gebied van herstel, bestaanszekerheid, groei en community.",
  });

  return (
    <div data-testid="voor-gemeenten-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Voor gemeenten & partners</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Samen bereiken we <em className="italic text-gold">meer</em>.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            FIN werkt samen met gemeenten, welzijnsorganisaties, woningcorporaties, maatschappelijke en zorgorganisaties,
            fondsen en bedrijven — op preventie, herstel, bestaanszekerheid, participatie en community-projecten.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-7 py-3.5 text-[15px] font-semibold shadow-gold"
              data-testid="gemeenten-cta-kennismaking"
            >
              Plan een kennismaking <ArrowUpRight size={15} />
            </Link>
            <Link
              to="/projecten"
              className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-7 py-3.5 text-[15px] font-semibold"
            >
              Bekijk lopende projecten
            </Link>
          </div>
        </div>
      </section>

      {/* Mogelijkheden */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Drie manieren om samen te werken</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Doorverwijzen. <em className="italic text-gold">Partneren.</em> Financieren.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {mogelijkheden.map((m) => {
              const Icon = m.icon;
              return (
                <article
                  key={m.label}
                  className="bg-sandSoft border border-line rounded-3xl p-7 flex flex-col hover:shadow-card transition"
                  data-testid={`mogelijkheid-${m.label.toLowerCase()}`}
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-2xl text-petrol leading-tight mb-3">{m.label}</h3>
                  <p className="text-inkMuted leading-relaxed text-[15px] text-pretty mb-5 flex-1">{m.body}</p>
                  <Link
                    to={m.to}
                    className="inline-flex items-center gap-1.5 text-gold font-semibold text-sm hover:text-goldDark transition"
                  >
                    {m.cta} <ArrowUpRight size={14} />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Propositie */}
      <section className="py-20 md:py-24 bg-sand">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Waarom FIN?</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Vijf redenen. <em className="italic text-gold">Eén stichting.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {propositie.map((p) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.label}
                  className="bg-ivory border border-line rounded-3xl p-7 hover:shadow-card transition"
                  data-testid={`propositie-${p.label.toLowerCase().replace(/[^\w]+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-2xl text-petrol leading-tight mb-2">{p.label}</h3>
                  <p className="text-inkMuted leading-relaxed text-[15px] text-pretty">{p.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Werkwijze */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Onze werkwijze</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Van gesprek <em className="italic text-gold">naar pilot</em>.
            </h2>
          </div>
          <ol className="lg:col-span-7 space-y-4">
            {[
              { t: "1", l: "Verkennend gesprek", d: "We luisteren naar uw vraag, uw doelgroep en wat er lokaal speelt." },
              { t: "2", l: "Voorstel op maat", d: "Binnen enkele weken een concreet pilotvoorstel: scope, aanpak, tijd, kosten en evaluatie." },
              { t: "3", l: "Kleinschalig starten", d: "We starten een pilot met een beperkte groep. Snel leren, snel bijsturen." },
              { t: "4", l: "Meten en rapporteren", d: "We evalueren zorgvuldig en delen inzichten in leesbare rapportages." },
              { t: "5", l: "Opschalen wat werkt", d: "Aantoonbaar werkende onderdelen schalen we op — samen met u." },
            ].map((s) => (
              <li key={s.t} className="flex items-start gap-4 bg-sandSoft border border-line rounded-2xl p-5">
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gold text-petrol font-display italic text-lg shrink-0">
                  {s.t}
                </span>
                <div>
                  <p className="font-display text-xl text-petrol leading-tight mb-1">{s.l}</p>
                  <p className="text-inkMuted text-[14.5px] leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-sand">
        <div className="container-x">
          <div className="bg-petrol text-cream rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/20 blur-3xl" />
            <div className="relative max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Klaar om samen te werken?</p>
              <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-balance mb-4">
                Eén gesprek. Meestal genoeg om richting te bepalen.
              </h2>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold shadow-gold"
                  data-testid="gemeenten-cta-plan"
                >
                  Plan een kennismaking <ArrowUpRight size={15} />
                </Link>
                <a
                  href="mailto:info@stichting-fin.nl"
                  className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-6 py-3 text-sm font-semibold"
                >
                  info@stichting-fin.nl
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
